"use client";

import { useEffect, useRef } from "react";
import { addToCartAction } from "@/app/[locale]/(shop)/cart/actions";
import { useCart } from "@/components/cart/CartContext";

/**
 * WebMCP trial (TRY-461): expose the storefront to in-browser agents as three
 * structured tools — `search_products`, `get_cart`, `add_to_cart` — instead of
 * leaving them to scrape a bilingual RTL DOM.
 *
 * Everything here is a thin client over surfaces that already exist and already
 * enforce themselves server-side: `/api/search` (public, rate-limited),
 * the `CartContext` mirror, and `addToCartAction` (refuses view-only stores and
 * validates every field; the cookie is validated on read). No tool grants
 * anything a shopper's browser could not already do.
 *
 * The API is a Chrome 149–156 origin trial and renamed itself once already
 * (`navigator.modelContext` → `document.modelContext`, Chrome 150 keeps the old
 * name as an alias) — so this feature-detects both and renders nothing, a no-op
 * in every other browser. No origin-trial token is registered; that is an ops
 * step needed only to demo in stable Chrome.
 */

interface ToolResult {
	content: { type: "text"; text: string }[];
}

interface ModelContextTool {
	name: string;
	description: string;
	inputSchema: Record<string, unknown>;
	annotations?: { readOnlyHint?: boolean };
	execute: (input?: Record<string, unknown>) => Promise<ToolResult>;
}

interface ModelContext {
	registerTool: (tool: ModelContextTool) => void;
	unregisterTool: (name: string) => void;
}

function modelContext(): ModelContext | undefined {
	const doc = document as Document & { modelContext?: ModelContext };
	const nav = navigator as Navigator & { modelContext?: ModelContext };
	return doc.modelContext ?? nav.modelContext;
}

function text(value: unknown): ToolResult {
	return { content: [{ type: "text", text: JSON.stringify(value) }] };
}

export function WebMcpTools() {
	const cart = useCart();
	// The tools close over a ref rather than the context value so registration
	// happens once and `get_cart` still reads the live cart. Updated in an
	// effect, not during render; tools only execute after commit, so the ref is
	// never stale when read.
	const cartRef = useRef(cart);
	useEffect(() => {
		cartRef.current = cart;
	}, [cart]);

	useEffect(() => {
		const mc = modelContext();
		if (!mc) return;

		mc.registerTool({
			name: "search_products",
			description:
				"Search this store's product catalog by keyword (Arabic or English, min 2 characters). Returns matching products with their id, name in both languages, price, image and availability, plus matching category suggestions. Use a returned product id with add_to_cart.",
			inputSchema: {
				type: "object",
				properties: {
					query: { type: "string", description: "Search terms, at least 2 characters" },
				},
				required: ["query"],
			},
			annotations: { readOnlyHint: true },
			execute: async (input) => {
				const q = typeof input?.query === "string" ? input.query : "";
				const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`);
				if (!res.ok) return text({ error: `search failed (${res.status})` });
				return text(await res.json());
			},
		});

		mc.registerTool({
			name: "get_cart",
			description:
				"Read the shopper's current cart: line items with product id, name in both languages, quantity and unit price, plus the item count and total.",
			inputSchema: { type: "object", properties: {} },
			annotations: { readOnlyHint: true },
			execute: async () => {
				const { items, count, total, viewOnly } = cartRef.current;
				return text({
					count,
					total,
					viewOnly,
					items: items.map((i) => ({
						productId: i.productId,
						variantId: i.variantId,
						name: i.name,
						qty: i.qty,
						unitPrice: i.price,
						note: i.note,
					})),
				});
			},
		});

		mc.registerTool({
			name: "add_to_cart",
			description:
				"Add a product to the shopper's cart by product id (from search_products). Refused when the store is view-only, and refused server-side for any product whose search result has requiresSelection: true. Those carry variants or required add-on groups and can only be added from their own product page.",
			inputSchema: {
				type: "object",
				properties: {
					productId: { type: "string", description: "The product id returned by search_products" },
					qty: { type: "integer", minimum: 1, maximum: 999, description: "Quantity, default 1" },
				},
				required: ["productId"],
			},
			execute: async (input) => {
				if (cartRef.current.viewOnly)
					return text({ ok: false, error: "This store is view-only and takes no orders." });
				const productId = typeof input?.productId === "string" ? input.productId.trim() : "";
				if (!productId) return text({ ok: false, error: "productId is required." });
				const qty = typeof input?.qty === "number" && Number.isInteger(input.qty) ? input.qty : 1;
				const fd = new FormData();
				fd.set("productId", productId);
				fd.set("qty", String(qty));
				// The action writes the cookie and revalidates; the CartProvider adopts
				// the server's cart on the next render, same as the normal client path.
				await addToCartAction(fd);
				// ⚠️ The action answers `void` — React's requirement for a
				// `<form action={…}>` (FORMS.md §11) — so there is nothing to read back
				// and this must not claim the line landed. It is refused for a product
				// with an unsatisfied required selection (TRY-469); `get_cart` is the
				// only truthful confirmation.
				return text({
					submitted: true,
					productId,
					qty,
					note: "Confirm with get_cart: an add is refused for a product that requires options.",
				});
			},
		});

		return () => {
			for (const name of ["search_products", "get_cart", "add_to_cart"]) mc.unregisterTool(name);
		};
	}, []);

	return null;
}
